import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as auth0 from 'auth0-js';
import { environment } from '../../../environments/environment';
import { AngularFireAuth } from "@angular/fire/auth"
import { Observable } from 'rxjs';
/**
  * This service is used to manage the authentication, including login, logout, renew token and firebase authentication
*/
@Injectable()
export class Auth0Service {

  private _idToken: string;
  private _accessToken: string;
  private _expiresAt: number;
  private _tokenType: string;
  private _idTokenPayload: any;
  private _firbaseAuth: AngularFireAuth;
  /**
    * Adding auth0 config to auth0 variable 
  */
  auth0 = new auth0.WebAuth(environment.auth0Config);

  constructor(public router: Router, public firebaseAuth: AngularFireAuth) {
    const tokenInfo = JSON.parse(localStorage.getItem('tokenInfo')) || {};
    this._idToken = tokenInfo.idToken || '';
    this._accessToken = tokenInfo.accessToken || '';
    this._expiresAt = tokenInfo.expiresAt || 0;
    this._tokenType = tokenInfo.tokenType || '';
    this._firbaseAuth = firebaseAuth;
    this._idTokenPayload = tokenInfo.idTokenPayload || {};
    if(this.isAuthenticated) {
      this._firebaseLogin()
    } else {
      this.logout();
    }
  }

  /**
    * To get the access token 
  */
  get accessToken(): string {
    return this._accessToken;
  }

  get idTokenDetail(): {}{
    return this._idTokenPayload;
  }

  /**
    * To get the token type
  */
  get tokenType(): string {
    return this._tokenType;
  }

  /**
    * To get the id token
  */
  get idToken(): string {
    return this._idToken;
  }

  /**
    * Call the auth0 login function
  */
  public login(): void {
    this.auth0.authorize();
  }

  /**
    * This function is used to check whether the user is all ready logged into the system
    * 
    * If the user is already logged in then it calls the localLogin() and checks for deeplinking
    * 
    * If there is no deeplinking then it calls the default route
    * 
    * If the user is not logged in then it will redirect the user to login screen
  */
  public handleAuthentication(): void {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.localLogin(authResult).then(()=>{
          const deeplink = sessionStorage.getItem('deeplink');
          if(deeplink){
            sessionStorage.clear();
            this.router.navigateByUrl(deeplink);
          }else{
            this.router.navigate(['/app/dashboard/board']);
          }
        }).catch(()=>{
          this.router.navigate(['']);
        })
      } else if (err) {
        this.router.navigate(['']);
        console.log("error : ", err);
      }
    });
  }

  /**
    * This function is used to call the firebase login using idTokenPayload
  */
  private _firebaseLogin(){
    return new Promise((resolve, reject)=>{
      const firebaseToken = this._idTokenPayload && this._idTokenPayload[`${environment.auth0Config.audience}firebase_token`]
      if (firebaseToken) {
        try {
          this._firbaseAuth.signInWithCustomToken(firebaseToken).then(result => {
            // firebase auth successful
            if (environment.production) {
              console.log("Firebase Auth successful")
            } else {
              console.log("Firebase Auth successful: ", result)
            }
            resolve(result);
          }).catch(error => {
            console.error("error while signing in with custom token", error)
            reject(error);
          })
        } catch (error) {
          console.error("Error calling auth", error);
          reject(error);
        }
      }
    })
  }

  /**
    * If the user is already logged in then get the data and bind that to the variables and call the firebase login function
  */
  private localLogin(authResult){
    // Set the time that the Access Token will expire at
    const expiresAt = (authResult.expiresIn * 1000) + Date.now();
    this._accessToken = authResult.accessToken;
    this._idToken = authResult.idToken;
    this._expiresAt = expiresAt;
    this._tokenType = authResult.tokenType;
    this._idTokenPayload = authResult.idTokenPayload

    const info = {
      accessToken: this._accessToken,
      idToken: this._idToken,
      expiresAt: this._expiresAt,
      tokenType: this._tokenType,
      idTokenPayload: this._idTokenPayload
    };
    localStorage.setItem("tokenInfo", JSON.stringify(info));
    return new Promise((resolve, reject)=>{
      this._firebaseLogin().then(result=>{
        resolve(result)
      }).catch(error=>{
        reject(error)
      })
    })
    
  }

  /**
    * This function is used to call the renewToken from auth0 package, if there is no accessToken available then redirect the user to login screen else call the localLogin function
  */
  public renewTokens(): Observable<string> {
    return Observable.create(observer => {
      this.auth0.checkSession({}, (err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          return this.localLogin(authResult).then(res=>{
            observer.next(authResult.accessToken);
            observer.complete();
          })
        } else if (err) {
          this.logout();
        }
      })
    })
  }

  /**
    * This function is used to logout the user from the system and clear the localstorage
  */
  public logout(): void {
    // Remove tokens and expiry time
    this._accessToken = '';
    this._idToken = '';
    this._expiresAt = 0;
    this._tokenType = '';
    this.clearLocalStorage();

    this.auth0.logout({
      returnTo: window.location.origin
    });
  }

  clearLocalStorage(){
    localStorage.removeItem('tokenInfo')
  }

  /**
    * Check whether the user is authenticated or not
  */
  public isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    const date = new Date().getMilliseconds();
    return this._accessToken && (date < this._expiresAt);
  }

  setTokenFromLandingPage(accessToken, type){
    this.clearLocalStorage();
    this._accessToken = accessToken;
    this._tokenType = type;

    const info = {
      accessToken: this._accessToken,
      tokenType: this._tokenType
    };
    localStorage.setItem("tokenInfo", JSON.stringify(info));
  }
}
